import { useEffect, useMemo, useState } from "react";
import { Search } from "lucide-react";
import { api } from "@/lib/api";
import { PageHero } from "@/components/PageHero";
import { SermonCard } from "@/components/SermonCard";
import { useLang } from "@/context/LangContext";
import { PageLayout } from "@/components/PageLayout";

const CATS = ["all", "sermon", "devotional", "music", "youth"];

export default function Sermons() {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [cat, setCat] = useState("all");
  const [q, setQ] = useState("");
  const [pastor, setPastor] = useState("all");

  useEffect(() => {
    api.get("/sermons").then((r) => setItems(Array.isArray(r.data) ? r.data : [])).catch(() => setItems([]));
  }, []);

  const pastors = useMemo(() => [...new Set(items.map((x) => x.speaker?.trim()).filter(Boolean))].sort(), [items]);
  const shown = useMemo(() => {
    const s = q.toLowerCase();
    return items.filter((x) => (cat === "all" || x.category === cat) && (pastor === "all" || x.speaker?.trim() === pastor) && [x.title_en, x.title_mh, x.speaker, x.scripture].join(" ").toLowerCase().includes(s));
  }, [items, cat, q, pastor]);

  return (
    <main data-testid="sermons-page">
      <PageLayout page="sermons" sections={{
        hero: <PageHero testid="sermons-hero" over={t("sermons.over")} title={t("sermons.title")} lead={t("sermons.lead")} />,
        list: (
      <section className="mx-auto max-w-[1400px] px-5 pb-24 md:px-10">
        <div className="sticky top-24 z-30 -mx-5 mb-10 flex flex-col gap-4 border-y border-[#F4EEE2]/10 bg-[#0B111E]/85 px-5 py-4 backdrop-blur-xl md:top-28 md:mx-0 md:flex-row md:items-center md:justify-between md:rounded-full md:border md:px-3 md:py-2">
          <div className="flex gap-2 overflow-x-auto">
            {CATS.map((c) => (
              <button
                key={c}
                data-testid={`sermon-category-tab-${c}`}
                onClick={() => setCat(c)}
                className={`shrink-0 rounded-full px-4 py-2 font-ui text-sm transition-colors duration-300 ${cat === c ? "bg-[#D4AF37] font-semibold text-[#0B111E]" : "text-[#F4EEE2]/80 hover:bg-[#F4EEE2]/5"}`}
              >
                {t(`cat.${c}`)}
              </button>
            ))}
          </div>
          <div className="flex flex-col gap-3 sm:flex-row md:items-center">
            {pastors.length > 0 && (
              <select data-testid="sermon-pastor-filter" value={pastor} onChange={(e) => setPastor(e.target.value)} className="rounded-full border border-[#F4EEE2]/15 bg-transparent px-4 py-2 font-ui text-sm text-[#F4EEE2] outline-none">
                <option value="all">{t("sermons.pastor")}</option>
                {pastors.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            )}
            <label className="flex items-center gap-2 rounded-full border border-[#F4EEE2]/15 px-4 py-2 md:w-80">
              <Search className="h-4 w-4 text-[#B7BFCC]" />
              <input data-testid="sermon-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("sermons.search")} className="w-full bg-transparent text-sm text-[#F4EEE2] outline-none placeholder:text-[#9AA4B4]" />
            </label>
          </div>
        </div>
        {shown.length ? (
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {shown.map((s, i) => <SermonCard key={s.id} s={s} index={i} />)}
          </div>
        ) : (
          <p data-testid="sermons-empty" className="py-20 text-center font-display text-3xl italic text-[#B7BFCC]">{t("sermons.empty")}</p>
        )}
      </section>
        ),
      }} />
    </main>
  );
}
